/* import __COLOCATED_TEMPLATE__ from './disable-discount-code-modal.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import ajax from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'ember-intl/services/intl';
import type { DiscountCode } from './types';

interface Signature {
  Args: {
    isOpen: boolean;
    onModalClose: () => void;
    discountCode: DiscountCode;
    shopifyDomain: string | null;
    appId: string;
    conversationId: number;
    onDisableSuccess: () => void;
  };
}

export default class DisableDiscountCodeModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @tracked reason = '';

  get isDisabling(): boolean {
    return taskFor(this.disableDiscountCodeTask).isRunning;
  }

  get formattedValue(): string {
    let value = Math.abs(parseFloat(this.args.discountCode.value));
    if (this.args.discountCode.value_type === 'percentage') {
      return `${value}%`;
    }
    return `$${value}`;
  }

  @action
  updateReason(event: Event): void {
    this.reason = (event.target as HTMLTextAreaElement).value;
  }

  @task({ restartable: true })
  *disableDiscountCodeTask(): TaskGenerator<void> {
    try {
      yield ajax({
        url: '/ember/inbox/shopify/disable_discount_code',
        type: 'POST',
        data: JSON.stringify({
          app_id: this.args.appId,
          shopify_domain: this.args.shopifyDomain,
          discount_id: this.args.discountCode.id,
          discount_code: this.args.discountCode.code,
          reason: this.reason,
          conversation_id: this.args.conversationId,
        }),
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'inbox.conversation-details-sidebar.shopify.disable-discount-code-modal.success',
        ),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'disable_discount_code',
        object: 'shopify_discount_code_action',
        section: 'inbox_sidebar',
        shopify_domain: this.args.shopifyDomain,
      });

      this.reason = '';
      this.args.onDisableSuccess();
    } catch (error: unknown) {
      captureException(error);
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.disable-discount-code-modal.error',
        ),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::DisableDiscountCodeModal': typeof DisableDiscountCodeModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/disable-discount-code-modal': typeof DisableDiscountCodeModal;
  }
}
